import { EmptyArray } from 'lib/array/arrayUtils'
import { capitaliseAll } from 'lib/string/stringUtils'
import type { FlightServiceSelectionServiceType } from 'checkout/selectors/view/flightServicesSelection'
import { getFlightSeatsKey } from 'lib/flights/flightUtils'
import type { IntlShape } from 'react-intl'
import { defineMessages } from 'react-intl'

type SeatSelections = Record<string, Record<string, App.FlightsSeatsItem>>

export type SeatSelectionsByKey = Record<string, SeatSelections>

// baggageKey -> flightId (leg) -> travellerId -> selection (mirrors the seats slice nesting).
type BaggageSelections = Record<string, Record<string, App.FlightBaggageSelection>>

export type BaggageSelectionsByKey = Record<string, BaggageSelections>

interface JourneyServiceAddon {
  name?: string
  amount?: number
  price?: number | { amount?: number }
  total?: number | { amount?: number }
  value?: number | { amount?: number }
  cost?: number | { amount?: number }
}

type JourneyExtrasWithServices = App.JourneyAddons & {
  services?: Array<JourneyServiceAddon>
}

type FlightItemViewJourneyWithServices = App.Checkout.FlightItemViewJourney & {
  services?: Array<JourneyServiceAddon>
}

export interface FlightServiceRowData {
  key: string
  label: string
  amount: number
}

const SERVICE_LABEL_MESSAGES = defineMessages({
  seat: {
    id: 'd_tZ7BAtg-1Iwnoi_G8q',
    defaultMessage: 'Seat selection',
    description: 'Label for seat selection flight service in checkout summary',
  },
  carry_on_baggage: {
    id: 'FzdgMJhgtXkFptgTHCZv',
    defaultMessage: 'Carry-on baggage',
    description: 'Label for carry-on baggage flight service in checkout summary',
  },
  checked_baggage: {
    id: 'RYqRCaMidt7b0MEHMY6W',
    defaultMessage: 'Checked baggage',
    description: 'Label for checked baggage flight service in checkout summary',
  },
  meal: {
    id: 'S20Y_SY7jcTUJNMfZlT3',
    defaultMessage: 'Meal selection',
    description: 'Label for meal selection flight service in checkout summary',
  },
})

const DEFAULT_FLIGHT_SERVICE_LABELS: Record<FlightServiceSelectionServiceType, string> = {
  seat: SERVICE_LABEL_MESSAGES.seat.defaultMessage ?? 'Seat selection',
  carry_on_baggage: SERVICE_LABEL_MESSAGES.carry_on_baggage.defaultMessage ?? 'Carry-on baggage',
  checked_baggage: SERVICE_LABEL_MESSAGES.checked_baggage.defaultMessage ?? 'Checked baggage',
  meal: SERVICE_LABEL_MESSAGES.meal.defaultMessage ?? 'Meal selection',
}

export function getFlightServiceLabels(intl: IntlShape): Record<FlightServiceSelectionServiceType, string> {
  return {
    seat: intl.formatMessage(SERVICE_LABEL_MESSAGES.seat),
    carry_on_baggage: intl.formatMessage(SERVICE_LABEL_MESSAGES.carry_on_baggage),
    checked_baggage: intl.formatMessage(SERVICE_LABEL_MESSAGES.checked_baggage),
    meal: intl.formatMessage(SERVICE_LABEL_MESSAGES.meal),
  }
}

const SERVICE_DISPLAY_ORDER: Array<FlightServiceSelectionServiceType> = [
  'seat',
  'carry_on_baggage',
  'checked_baggage',
  'meal',
]

export function buildFlightServiceRows(
  itemBreakdownView: App.Checkout.FlightItemBreakdownView,
  seatSelections?: SeatSelections,
  baggageSelections?: BaggageSelections,
): Array<FlightServiceRowData> {
  const itemView = itemBreakdownView.itemView
  if (!itemView) { return [] }

  const passengers = itemView.passengers ?? EmptyArray

  const totals = new Map<string, number>()

  // New Sabre checked-baggage total from the `baggageSelections` store. When present it takes
  // precedence over the legacy `PaxBaggageMap` flow so a single item never double-counts.
  const baggageAddonTotal = baggageSelections
    ? computeBaggageSelectionTotal(baggageSelections)
    : 0

  itemView.flights.forEach((flightView) => {
    const flight = flightView as FlightItemViewJourneyWithServices
    const { journeyFlight } = flight
    const flightExtras = journeyFlight?.extras as JourneyExtrasWithServices | undefined
    const selectedExtras = flight.extras

    if (flightExtras) {
      const legacyBaggageSelections = selectedExtras?.baggage
      const carryOnSelections = selectedExtras?.carryOnBaggage

      if (baggageAddonTotal <= 0) {
        const checkedCost = computeSelectedAddonCost(
          flightExtras.baggage,
          legacyBaggageSelections,
          passengers,
        )
        addServiceTotal(totals, 'checked_baggage', checkedCost)
      }

      const carryOnCost = computeSelectedAddonCost(
        flightExtras.carryOnBaggage,
        carryOnSelections,
        passengers,
      )
      addServiceTotal(totals, 'carry_on_baggage', carryOnCost)
    }

    const serviceAddons: Array<JourneyServiceAddon> = [
      ...(flightExtras?.services ?? EmptyArray),
      ...(flight.services ?? EmptyArray),
    ]
    serviceAddons.forEach((service) => {
      addServiceTotal(totals, normaliseServiceKey(service.name), extractServiceAmount(service))
    })
  })

  if (seatSelections) {
    const seatTotal = computeSeatSelectionTotal(seatSelections, passengers)
    addServiceTotal(totals, 'seat', seatTotal)
  }

  addServiceTotal(totals, 'checked_baggage', baggageAddonTotal)

  const orderedKeys = [
    ...SERVICE_DISPLAY_ORDER.filter(key => totals.has(key)),
    ...Array.from(totals.keys()).filter(key => !SERVICE_DISPLAY_ORDER.includes(key as FlightServiceSelectionServiceType)),
  ]

  return orderedKeys
    .map((key) => {
      const amount = totals.get(key) ?? 0
      if (amount <= 0) { return undefined }

      return {
        key,
        label: formatServiceLabel(key),
        amount,
      }
    })
    .filter((row): row is FlightServiceRowData => Boolean(row))
}

function addServiceTotal(
  totals: Map<string, number>,
  key: FlightServiceSelectionServiceType | string | undefined,
  amount: number | undefined,
) {
  if (!key || !amount || amount <= 0) { return }

  const current = totals.get(key) ?? 0
  totals.set(key, current + amount)
}

function computeSelectedAddonCost(
  addons: Array<App.JourneyAddon> | undefined,
  selections: App.Checkout.PaxBaggageMap | undefined,
  passengers: Array<App.Checkout.FlightPassenger>,
): number {
  if (!addons?.length || !selections || !passengers.length) { return 0 }

  const amountById = new Map(addons.map(addon => [addon.id, addon.amount ?? 0]))

  return passengers.reduce((total, passenger) => {
    const passengerSelections = selections[passenger.id]
    if (!passengerSelections) { return total }

    const passengerTotal = Object.entries(passengerSelections).reduce((acc, [addonId, quantity]) => {
      const amount = amountById.get(addonId) ?? 0
      if (amount <= 0) { return acc }

      const count = typeof quantity === 'number' ? quantity : Number(quantity)
      if (!Number.isFinite(count) || count <= 0) { return acc }

      return acc + amount * count
    }, 0)

    return total + passengerTotal
  }, 0)
}

function extractServiceAmount(service: JourneyServiceAddon | undefined): number {
  if (!service) { return 0 }

  const possibleValues = [service.amount, service.price, service.total, service.value, service.cost]
  for (const value of possibleValues) {
    const numeric = normaliseAmount(value)
    if (numeric > 0) { return numeric }
  }

  return 0
}

function formatServiceLabel(name: string): string {
  const normalisedName = normaliseServiceKey(name) as FlightServiceSelectionServiceType | undefined
  if (normalisedName && normalisedName in DEFAULT_FLIGHT_SERVICE_LABELS) {
    return DEFAULT_FLIGHT_SERVICE_LABELS[normalisedName]
  }

  return capitaliseAll(name)
}

function computeSeatSelectionTotal(
  seatSelections: SeatSelections,
  passengers: Array<App.Checkout.FlightPassenger>,
): number {
  const passengerTypeById = new Map(
    passengers.map(p => [p.id, p.type]),
  )

  return Object.values(seatSelections).reduce((segmentTotal, travellerSeats) => {
    const travellerTotal = Object.entries(travellerSeats).reduce((acc, [travellerId, seat]) => {
      const passengerType = passengerTypeById.get(travellerId)
      const priceByType = passengerType
        ? seat.pricesByPassengerType?.[passengerType]?.totalAmount?.amount
        : undefined
      const amount = priceByType ?? seat.unitPrice?.totalAmount?.amount ?? 0
      return amount > 0 ? acc + amount : acc
    }, 0)

    return segmentTotal + travellerTotal
  }, 0)
}

function computeBaggageSelectionTotal(
  baggageSelections: BaggageSelections,
): number {
  // Nested per leg: flightId -> travellerId -> selection. Sum every selection across all legs.
  return Object.values(baggageSelections).reduce((total, travellerSelections) => {
    return Object.values(travellerSelections).reduce((legTotal, selection) => {
      const amount = selection?.amount ?? 0
      return amount > 0 ? legTotal + amount : legTotal
    }, total)
  }, 0)
}

export function normaliseServiceKey(key: string | undefined): string | undefined {
  if (!key) { return undefined }

  const trimmed = key.trim()
  if (!trimmed) { return undefined }

  const normalised = trimmed.toLowerCase().replace(/\s+/g, '_').replace(/-+/g, '_')

  switch (normalised) {
    case 'seat':
    case 'seats':
      return 'seat'
    case 'carry_on':
    case 'carry_on_baggage':
    case 'carry_on_bag':
    case 'carry-on_baggage':
      return 'carry_on_baggage'
    case 'checked_baggage':
    case 'checked_luggage':
    case 'checked_luggage_baggage':
    case 'checked-baggage':
      return 'checked_baggage'
    case 'meal':
    case 'meals':
      return 'meal'
    default:
      return normalised
  }
}

function normaliseAmount(value: unknown): number {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0
  }

  if (typeof value === 'string') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : 0
  }

  if (value && typeof value === 'object') {
    const amountValue = (value as { amount?: unknown }).amount
    if (amountValue !== undefined) {
      return normaliseAmount(amountValue)
    }
  }

  return 0
}

const SERVICES_ORDER = ['seat', 'carry_on_baggage', 'checked_baggage', 'meal']

function getItemDedupeKey(
  item: App.Checkout.FlightItemBreakdownView,
): string | undefined {
  const checkoutItem = item.itemView?.item
  if (!checkoutItem?.searchId) {
    return undefined
  }

  const flightsWithJourneyIds = checkoutItem.flights.filter(flight => Boolean(flight.journeyId))
  const journeyIds = flightsWithJourneyIds
    .map(f => f.journeyId)
    .filter((id): id is string => Boolean(id))
  if (!journeyIds.length) {
    return undefined
  }

  const passengerCount = checkoutItem.passengers?.length ?? 0
  const fareFamilyIds = flightsWithJourneyIds.map(flight => flight.fareFamily?.id)
  return getFlightSeatsKey(checkoutItem.searchId, journeyIds, passengerCount, fareFamilyIds)
}

function getItemFallbackKey(
  item: App.Checkout.FlightItemBreakdownView,
): string | undefined {
  const flights = item.itemView?.flights ?? []
  const journeyKeys = flights
    .map(flight => flight.journeyFlight.journeyKey)
    .filter((key): key is string => Boolean(key))
  if (!journeyKeys.length) {
    return undefined
  }

  return JSON.stringify(journeyKeys.slice().sort())
}

function addRowsToTotals(
  totals: Map<string, { amount: number, label: string }>,
  rows: Array<FlightServiceRowData>,
): void {
  for (const row of rows) {
    const current = totals.get(row.key)
    totals.set(row.key, {
      amount: (current?.amount ?? 0) + row.amount,
      label: current?.label ?? row.label,
    })
  }
}

function sortTotalsToRows(
  totals: Map<string, { amount: number, label: string }>,
): Array<FlightServiceRowData> {
  const knownKeys = SERVICES_ORDER.filter(key => totals.has(key))
  const otherKeys = Array.from(totals.keys())
    .filter(key => !SERVICES_ORDER.includes(key))
    .sort()

  return [...knownKeys, ...otherKeys]
    .map(key => {
      const entry = totals.get(key)!
      return { key, label: entry.label, amount: entry.amount }
    })
    .filter(row => row.amount > 0)
}

export function aggregateFlightServiceRows(
  items: Array<App.Checkout.FlightItemBreakdownView>,
  seatSelectionsByKey: SeatSelectionsByKey,
  baggageSelectionsByKey: BaggageSelectionsByKey = {},
): Array<FlightServiceRowData> {
  if (!items.length) { return [] }

  const totals = new Map<string, { amount: number, label: string }>()
  const seen = new Set<string>()

  for (const item of items) {
    const dedupeKey = getItemDedupeKey(item)
    const fallbackKey = getItemFallbackKey(item)
    const key = dedupeKey ?? fallbackKey

    if (key && seen.has(key)) { continue }
    if (key) { seen.add(key) }

    const seatSelections = dedupeKey ? seatSelectionsByKey[dedupeKey] : undefined
    const baggageSelections = dedupeKey ? baggageSelectionsByKey[dedupeKey] : undefined
    const rows = buildFlightServiceRows(item, seatSelections, baggageSelections)
    addRowsToTotals(totals, rows)
  }

  return sortTotalsToRows(totals)
}

export const __testables = {
  computeSeatSelectionTotal,
  computeSelectedAddonCost,
  computeBaggageSelectionTotal,
}
